import { Component, OnInit, Input, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MerchantWhitelistedWalletDetailsComponent } from './merchant-whitelisted-wallet-details/merchant-whitelisted-wallet-details.component';
import { finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { WhitelistedWalletType, WhitelistedWalletStatusEnum } from '@olv/ui-shared/models/wallet.model';
import {
  WhitelistedWalletFilterParams,
  MerchantService,
  PagedWhitelistedWalletsResultDto,
  WhitelistedWalletCreateDto,
  WhitelistedWalletResponseDto,
  WhitelistedWalletValidateBody,
} from '@olv/ui-shared/services/merchant.service';
import { BlockchainDropdownComponent } from '@olv/ui-shared/ui-shared/blockchain-dropdown/blockchain-dropdown.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import {
  AppliedFilter,
  FilterService,
  WHITELISTED_WALLET_FILTER_ALIASES,
} from '@olv/ui-shared/services/filter.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { WhitelistedWallet, WhitelistedWalletSourceEnum } from './merchant-whitelisted-wallets.models';

@Component({
  selector: 'app-merchant-whitelisted-wallets',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    PillSearchComponent,
    TextInputComponent,
    FormContainerComponent,
    MerchantWhitelistedWalletDetailsComponent,
    BlockchainDropdownComponent,
    HasPermissionDirective,
    DropdownComponent,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    StateBadgeComponent,
    EmptyStateComponent,
    ButtonComponent,
  ],
  templateUrl: './merchant-whitelisted-wallets.component.html',
  styleUrls: ['./merchant-whitelisted-wallets.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MerchantWhitelistedWalletsComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly formatDateStandard = formatDateStandard;
  protected readonly shortenAddress = shortenAddress;

  showAddWalletForm = false;
  addWalletForm!: FormGroup;
  attachedFile: File | undefined = undefined;
  attachedFileName: string | null = null;
  submissionError: string | null = null;
  hasMoreWallets = false;
  whitelistedError = '';
  backendErrors: Record<string, string> = {};

  @Input() merchantId: string | null = null;

  // Pagination properties
  currentPage = 0; // 0-indexed for backend SkipCount calculation
  pageSize = 10; // Default page size

  whitelistedWallets: WhitelistedWallet[] = [];
  filteredWallets: WhitelistedWallet[] = [];
  totalWhitelistedAddresses = 0;

  whitelistedWalletTypeOptions = [
    { value: WhitelistedWalletType.SettlementPayout, label: 'Settlement Payout' },
    { value: WhitelistedWalletType.ClientPayout, label: 'Client Payout' },
    { value: WhitelistedWalletType.ServicePayout, label: 'Service Payout' },
  ];

  selectedWallet: WhitelistedWallet | null = null;

  searchText = '';
  filtersApplied = false;
  filterMap: Partial<WhitelistedWalletFilterParams> = {};
  private readonly whitelistedWalletFilterAliases = WHITELISTED_WALLET_FILTER_ALIASES;

  get filters() {
    return [
      ...(this.isSettlementApp
        ? [
            { name: 'Risk Level', filterFields: enumToFilterFields(RiskLevelEnum) },
            { name: 'Withdrawal Type', filterFields: enumToFilterFields(WhitelistedWalletType) },
          ]
        : []),
      { name: 'Blockchain', filterFields: this.metadata.filterFields() },
      {
        name: 'Status',
        filterFields: enumToFilterFields(WhitelistedWalletStatusEnum),
      },
    ];
  }

  readonly DEFAULT_WALLET_STATUSES: WhitelistedWalletStatusEnum[] = [
    WhitelistedWalletStatusEnum.Pending,
    WhitelistedWalletStatusEnum.Approved,
  ];

  isLoading = false;
  isFirstLoad = true;
  isDisableFilters = false;
  isMoreLoading = false;
  isValidating = false;
  isSettlementApp = false;
  error: string | null = null;

  private formErrorService = inject(FormErrorService);
  private merchantService = inject(MerchantService);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private readonly appCtx = inject(AppContextService);
  private dialog = inject(MatDialog);
  private filterService = inject(FilterService);
  protected readonly metadata = inject(NetworkMetadataService);

  ngOnInit(): void {
    this.metadata.load();
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement' ? true : false;

    this.applyDefaultFilter();
    this.initAddWalletForm();
  }
  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  private applyDefaultFilter(): void {
    this.filterMap = {
      walletStatuses: [...this.DEFAULT_WALLET_STATUSES],
    };

    this.filtersApplied = true;
    this.searchText = '';

    const filter: WhitelistedWalletFilterParams = {
      ...this.filterMap,
      skipCount: 0,
      maxResultCount: this.pageSize,
    };

    this.loadWalletsWithFilter(filter);
  }

  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.addWalletForm.get(controlName), fieldName);
  }

  get addWalletNextButtonState(): 'disabled' | 'default' {
    return this.addWalletForm.invalid || this.isValidating ? 'disabled' : 'default';
  }

  initAddWalletForm(): void {
    this.addWalletForm = this.fb.group({
      recipientAddress: this.fb.control('', {
        validators: [Validators.required, ValidationService.addressByChainValidator((type) => this.metadata.network(type))],
        updateOn: 'change',
      }),
      blockchain: this.fb.control('', {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      walletName: this.fb.control('', {
        validators: [Validators.required, ValidationService.textValidator(1, 60, '^[\\p{L}\\p{N} _-]*$')],
        updateOn: 'change',
      }),
      attachedDocument: this.fb.control(null, {
        validators: [
          ValidationService.fileTypeValidator(['.jpg', '.jpeg', '.png', '.pdf']),
          ValidationService.fileSizeValidator(),
        ],
        updateOn: 'change',
      }),
    });

    if (this.isSettlementApp) {
      this.addWalletForm.addControl(
        'whitelistedWalletType',
        this.fb.control('', {
          validators: [Validators.required],
          updateOn: 'change',
        }),
      );
    }

    // Re-validate the address whenever blockchain changes
    this.addWalletForm.get('blockchain')!.valueChanges.subscribe(() => {
      this.addWalletForm.get('recipientAddress')!.updateValueAndValidity({ emitEvent: false });
    });

    this.addWalletForm.get('recipientAddress')?.valueChanges.subscribe(() => {
      this.backendErrors['recipientAddress'] = '';
    });
    this.addWalletForm.get('walletName')?.valueChanges.subscribe(() => {
      this.backendErrors['walletName'] = '';
    });
  }

  loadWhitelistedWallets(loadMore = false): void {
    this.error = null;
    if (!loadMore) {
      this.currentPage = 0;
      this.whitelistedWallets = [];
      this.isLoading = true;
    } else {
      this.isMoreLoading = true;
    }
    const params = {
      skipCount: this.pageSize ? this.currentPage * this.pageSize : 0,
      merchantIdFilter: this.merchantId!,
      maxResultCount: this.pageSize,
    };

    this.merchantService.getWhitelistedWallets(params).subscribe({
      next: (response: PagedWhitelistedWalletsResultDto) => {
        const newItems = response.items.map((dto) => ({
          id: dto.id,
          walletAddress: dto.walletAddress,
          blockchain: dto.blockchain,
          walletName: dto.walletName,
          preparedBy: dto.preparedBy,
          date: new Date(dto.date),
          withdrawalType: dto.withdrawalType,
          riskLevel: (dto.riskLevel || 'Low') as WhitelistedWallet['riskLevel'],
          status: (dto.status || 'Pending') as WhitelistedWallet['status'],
          documentId: dto.documentId,
          documentName: dto.documentName,
          comments: dto.comments,
          ownerType: dto.ownerType,
          riskAssessmentCompleted: dto.riskAssessmentCompleted,
          canApproveOrReject: dto.canApproveOrReject,
          source: dto.source,
        }));
        this.totalWhitelistedAddresses = response.totalCount;
        this.whitelistedWallets = loadMore ? [...this.whitelistedWallets, ...newItems] : newItems;
        this.filteredWallets = this.whitelistedWallets;
        this.hasMoreWallets = this.whitelistedWallets.length < response.totalCount;

        if (this.isFirstLoad) {
          this.isDisableFilters = this.whitelistedWallets.length === 0 && !this.searchText?.trim();
        }
        this.isFirstLoad = false;
        this.isLoading = false;
        this.isMoreLoading = false;
      },
      error: (err) => {
        this.error = `Failed to load whitelisted wallets: ${err.message || 'Unknown server error'}`;
        this.isLoading = false;
        this.isMoreLoading = false;
        this.whitelistedWallets = []; // Clear data on error
        this.filteredWallets = [];
        this.totalWhitelistedAddresses = 0;
      },
    });
  }
  loadWalletsWithFilter(filter: WhitelistedWalletFilterParams, loadMore = false): void {
    filter.merchantIdFilter = this.merchantId!;

    if (!loadMore) {
      this.currentPage = 0;
      this.whitelistedWallets = [];
      this.isLoading = true;
    } else {
      this.isMoreLoading = true;
    }
    this.error = null;

    this.merchantService.getWhitelistedWallets(filter).subscribe({
      next: (response: PagedWhitelistedWalletsResultDto) => {
        const newItems = response.items.map((dto) => ({
          id: dto.id,
          walletAddress: dto.walletAddress,
          blockchain: dto.blockchain,
          walletName: dto.walletName,
          preparedBy: dto.preparedBy,
          date: new Date(dto.date),
          withdrawalType: dto.withdrawalType,
          riskLevel: (dto.riskLevel || 'Low') as WhitelistedWallet['riskLevel'],
          status: (dto.status || 'Pending') as WhitelistedWallet['status'],
          documentId: dto.documentId,
          documentName: dto.documentName,
          comments: dto.comments,
          ownerType: dto.ownerType,
          riskAssessmentCompleted: dto.riskAssessmentCompleted,
          canApproveOrReject: dto.canApproveOrReject,
          source: dto.source,
        }));
        this.totalWhitelistedAddresses = response.totalCount;
        this.whitelistedWallets = loadMore ? [...this.whitelistedWallets, ...newItems] : newItems;
        this.filteredWallets = this.whitelistedWallets;
        this.hasMoreWallets = this.whitelistedWallets.length < response.totalCount;
        this.isLoading = false;
        this.isMoreLoading = false;
      },
      error: (err) => {
        this.error = `Failed to load whitelisted wallets: ${err.message || 'Unknown server error'}`;
        this.isLoading = false;
        this.whitelistedWallets = [];
        this.filteredWallets = [];
        this.totalWhitelistedAddresses = 0;
      },
    });
  }
  loadMoreWallets(): void {
    if (!this.isLoading && this.hasMoreWallets) {
      this.currentPage++;
      if (this.filtersApplied) {
        this.loadWalletsWithFilter(this.filterMap, true);
      } else {
        this.loadWhitelistedWallets(true);
      }
    }
  }

  /**
   * Apply all filters to the whitelisted wallets data
   */

  onFiltersApplied(selectedValues: AppliedFilter[]): void {
    this.filtersApplied = selectedValues.length > 0 ? true : false;
    this.filterMap = this.filterService.applySelectedFilters(
      selectedValues,
      {},
      this.whitelistedWalletFilterAliases,
    );

    const filter: WhitelistedWalletFilterParams = {
      ...this.filterMap,
      skipCount: 0,
      maxResultCount: this.pageSize,
      filter: this.searchText?.trim() || undefined,
      merchantIdFilter: this.merchantId!,
    };

    this.loadWalletsWithFilter(filter);
  }

  /**
   * Handle search text changes
   * @param searchText The updated search text
   */
  onSearchChange(searchState: SearchState): void {
    if (this.searchText === searchState.value) {
      return;
    }

    this.searchText = searchState.value;

    const filter: WhitelistedWalletFilterParams = {
      ...this.filterMap,
      skipCount: 0,
      maxResultCount: this.pageSize,
      filter: searchState.value?.trim() || undefined,
    };

    this.loadWalletsWithFilter(filter);
  }

  /**
   * Clear all active filters
   */
  clearFilters(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadWhitelistedWallets();
  }

  /**
   * Handle Add Address button click
   */
  onAddAddress(): void {
    this.showAddWalletForm = true;
    this.attachedFile = undefined;
    this.attachedFileName = null;
    this.selectedWallet = null;
    this.merchantService.closeSidebar();
    this.addWalletForm.reset();
  }

  onCancelAddWallet(): void {
    this.showAddWalletForm = false;
    this.initAddWalletForm();
    this.removeAttachedFile();
    this.submissionError = null;
  }

  private onAddWallet(otp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    if (this.addWalletForm.invalid) {
      this.addWalletForm.markAllAsTouched();
      this.submissionError = 'Please fill in all required fields.';
      return;
    }
    if (this.isSettlementApp && !this.merchantId) {
      this.submissionError = 'Merchant ID is missing. Cannot submit the form.';
      return;
    }

    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    this.submissionError = null;

    const formValue = this.addWalletForm.value;
    const walletData: WhitelistedWalletCreateDto = {
      merchantId: this.merchantId || '',
      WalletAddress: formValue.recipientAddress,
      blockchain: formValue.blockchain,
      name: formValue.walletName,
      ...(this.isSettlementApp
        ? {
            whitelistedWalletType: Number(formValue.whitelistedWalletType) as WhitelistedWalletType,
          }
        : {}),
    };

    this.merchantService
      .addWhitelistedWallet(walletData, otp, this.attachedFile)
      .pipe(
        finalize(() => {
          this.applyDefaultFilter();
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: (newWallet: WhitelistedWalletResponseDto) => {
          this.whitelistedWallets.unshift({
            id: newWallet.id,
            walletAddress: newWallet.walletAddress,
            blockchain: newWallet.blockchain,
            walletName: newWallet.walletName,
            preparedBy: newWallet.preparedBy,
            date: new Date(newWallet.date),
            withdrawalType: newWallet.withdrawalType,
            ownerType: newWallet.ownerType,
            riskLevel: (newWallet.riskLevel || 'Low') as WhitelistedWallet['riskLevel'],
            status: (newWallet.status || 'Pending') as WhitelistedWallet['status'],
            documentId: newWallet.documentId,
            documentName: newWallet.documentName,
            riskAssessmentCompleted: newWallet.riskAssessmentCompleted,
            canApproveOrReject: newWallet.canApproveOrReject,
          });
          this.totalWhitelistedAddresses = this.whitelistedWallets.length;
          dialogRef.close();
          this.onCancelAddWallet();
        },
        error: (error) => {
          if (error.isOtpError) {
            dialog.setErrorState(error.error?.message || error.error);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          this.submissionError = error.error.message;
        },
      });
  }

  onFileSelected(event: Event): void {
    const fileInput = event.currentTarget as HTMLInputElement;
    const file = fileInput.files && fileInput.files.length > 0 ? fileInput.files[0] : null;
    const attachedDocument = this.addWalletForm.get('attachedDocument');

    if (!file || !attachedDocument) {
      return;
    }

    attachedDocument.setValue(file);
    attachedDocument.markAsTouched();
    attachedDocument.updateValueAndValidity();

    if (attachedDocument.valid) {
      this.attachedFile = file;
      this.attachedFileName = file.name;
    } else {
      this.attachedFile = undefined;
      this.attachedFileName = file.name;
    }

    fileInput.value = '';
  }

  removeAttachedFile(): void {
    this.attachedFile = undefined;
    this.attachedFileName = null;
    this.addWalletForm.get('attachedDocument')?.reset();
  }

  getStatusClass(status: WhitelistedWallet['status']): string {
    return `status-${status.toLowerCase()}`;
  }

  /**
   * Show transaction details in side panel
   * @param transaction The transaction to show details for
   */
  showWhitelistedWallet(wallet: WhitelistedWallet): void {
    const clickedOnSame = this.selectedWallet === wallet ? true : false;
    if (clickedOnSame) {
      this.closeWalletDetails();
    } else {
      setTimeout(() => {
        this.merchantService.openSidebar();
        this.selectedWallet = wallet;
        this.cd.detectChanges();
      }, 0);
    }
  }
  closeWalletDetails(): void {
    this.selectedWallet = null;
    this.merchantService.closeSidebar();
  }
  toggleFilters(): void {
    this.selectedWallet = null;
    this.merchantService.closeSidebar();

    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }
  getAppliedFiltersAsArray(): AppliedFilter[] {
    return this.filterService.getAppliedFilters(this.filterMap, this.whitelistedWalletFilterAliases);
  }
  get sidebarOpen() {
    return this.merchantService.sidebarOpenSignal();
  }
  onWalletUpdated(): void {
    // Do a refresh to the table after update
    if (this.filtersApplied) {
      this.loadWalletsWithFilter(this.filterMap);
    } else {
      this.loadWhitelistedWallets();
    }
  }

  openOTP(): void {
    const formValue = this.addWalletForm.getRawValue();

    const payload: WhitelistedWalletValidateBody = {
      MerchantId: this.merchantId!,
      NetworkType: Number(formValue.blockchain),
      WalletAddress: formValue.recipientAddress,
      WalletName: formValue.walletName,
    };

    this.isValidating = true;

    this.merchantService
      .validateWhitelistedWallet(payload)
      .pipe(finalize(() => (this.isValidating = false)))
      .subscribe({
        next: () => {
          const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            autoFocus: false,
            disableClose: false,
            panelClass: 'custom-dialog-container',
            data: {
              title: 'Verify Whitelisted Wallet',
            },
          });

          const sub = dialogRef.componentInstance.submitOtp.subscribe((otp) => {
            this.onAddWallet(otp, dialogRef);
          });

          dialogRef.afterClosed().subscribe(() => {
            sub.unsubscribe();
          });
        },
        error: (err: HttpErrorResponse) => {
          this.applyBackendValidationErrors(err);
        },
      });
  }

  private applyBackendValidationErrors(err: HttpErrorResponse) {
    const errors = err?.error?.errors as Record<string, string[]> | undefined;
    if (!errors) {
      this.backendErrors = {};
      return;
    }

    this.backendErrors = {
      recipientAddress: errors['WalletAddress']?.join(' ') || '',
      walletName: errors['WalletName']?.join(' ') || '',
    };

    this.cd.detectChanges();
  }
  onFilterReset(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadWhitelistedWallets();
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.isLoading;
    }

    return this.isDisableFilters;
  }
}
